// Connection graph for traced roof templates: which line ends meet, and
// what each meeting point means (mitre, stop end, starter, lap, apex...).
const RIDGE_LIKE = new Set(['ridge', 'hip']);
const LAP_LIKE = new Set(['ridge', 'hip', 'flashing', 'barge', 'apron', 'valley']);

const isLine = (s) => s && s.type !== 'point' && Array.isArray(s.points) && s.points.length >= 2;
const lt = (s) => String(s?.lineType || '').toLowerCase();

// Each vertex contributes "segment ends": 1 at an open end, 2 at an interior / closed vertex.
const vertexEnds = (s, pi) => {
  const n = s.points.length;
  if (s.closed && n >= 3) return 2;
  return pi === 0 || pi === n - 1 ? 1 : 2;
};

export const buildNodes = (strokes, tol) => {
  const nodes = [];
  strokes.forEach((s, si) => {
    if (!isLine(s)) return;
    s.points.forEach((p, pi) => {
      let node = nodes.find(n => Math.hypot(n.x - p.x, n.y - p.y) <= tol);
      if (!node) { node = { x: p.x, y: p.y, refs: [], ends: 0, types: [] }; nodes.push(node); }
      node.refs.push({ si, pi });
      node.ends += vertexEnds(s, pi);
      node.types.push(lt(s));
    });
  });
  return nodes;
};

const centroid = (strokes) => {
  let x = 0, y = 0, n = 0;
  strokes.forEach(s => { if (isLine(s)) s.points.forEach(p => { x += p.x; y += p.y; n += 1; }); });
  return n ? { x: x / n, y: y / n } : { x: 0, y: 0 };
};

// Neighbour vertex of (si, pi) along its stroke — used for corner / side maths.
const neighbours = (strokes, { si, pi }) => {
  const s = strokes[si];
  const out = [];
  if (pi > 0) out.push(s.points[pi - 1]);
  if (pi < s.points.length - 1) out.push(s.points[pi + 1]);
  if (s.closed && s.points.length >= 3) {
    if (pi === 0) out.push(s.points[s.points.length - 1]);
    if (pi === s.points.length - 1) out.push(s.points[0]);
  }
  return out;
};

// Standing outside the building looking at the gutter: is this end on my left?
const isLeftHand = (end, other, c) => {
  const mx = (end.x + other.x) / 2, my = (end.y + other.y) / 2;
  let nx = mx - c.x, ny = my - c.y;          // outward (away from roof centre)
  const fx = -nx, fy = -ny;                   // viewer faces the roof
  const lx = fy, ly = -fx;                    // viewer's left in screen coords (y down)
  nx = end.x - mx; ny = end.y - my;
  return nx * lx + ny * ly > 0;
};

export const classifyNode = (node, strokes, c = centroid(strokes)) => {
  const types = node.types;
  const all = (set) => types.every(t => set.has ? set.has(t) : t === set);
  const ends = node.ends;
  if (types.every(t => t === 'gutter')) {
    if (ends === 1) {
      const ref = node.refs[0];
      const other = neighbours(strokes, ref)[0];
      return { kind: 'stop_end', label: other && isLeftHand(node, other, c) ? 'Stop end LH' : 'Stop end RH' };
    }
    const nb = node.refs.flatMap(r => neighbours(strokes, r));
    if (nb.length >= 2) {
      const u = { x: nb[0].x - node.x, y: nb[0].y - node.y }, v = { x: nb[1].x - node.x, y: nb[1].y - node.y };
      const lu = Math.hypot(u.x, u.y) || 1, lv = Math.hypot(v.x, v.y) || 1;
      const bx = u.x / lu + v.x / lv, by = u.y / lu + v.y / lv;
      const external = bx * (c.x - node.x) + by * (c.y - node.y) > 0;
      return { kind: external ? 'mitre_external' : 'mitre_internal', label: external ? 'Mitre (external)' : 'Mitre (internal)' };
    }
    return { kind: 'mitre', label: 'Mitre' };
  }
  if (ends === 1) return { kind: 'loose', label: 'Loose end' };
  if (types.some(t => RIDGE_LIKE.has(t)) && types.includes('gutter')) return { kind: 'starter', label: 'Starter' };
  if (all(new Set(['valley']))) return { kind: 'valley_join', label: ends > 2 ? `Valley join (${ends})` : 'Valley join' };
  if (types.every(t => LAP_LIKE.has(t))) {
    return ends >= 3 ? { kind: 'apex', label: `Apex · ${ends} laps` } : { kind: 'lap', label: 'Join lap' };
  }
  return { kind: 'joined', label: `Joined (${ends})` };
};

const distToSeg = (p, a, b) => {
  const dx = b.x - a.x, dy = b.y - a.y;
  const L2 = dx * dx + dy * dy || 1;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / L2));
  const q = { x: a.x + t * dx, y: a.y + t * dy };
  return { d: Math.hypot(p.x - q.x, p.y - q.y), q };
};

// Nearest point on any gutter line (for snapping outlets onto gutters).
export const nearestGutterPoint = (strokes, p) => {
  let best = null;
  strokes.forEach(s => {
    if (!isLine(s) || lt(s) !== 'gutter') return;
    const pts = s.closed ? [...s.points, s.points[0]] : s.points;
    for (let i = 0; i < pts.length - 1; i += 1) {
      const r = distToSeg(p, pts[i], pts[i + 1]);
      if (!best || r.d < best.d) best = r;
    }
  });
  return best;
};

export const summariseConnections = (strokes, tol) => {
  const c = centroid(strokes);
  const nodes = buildNodes(strokes, tol).map(n => ({ ...n, ...classifyNode(n, strokes, c) }));
  const counts = {};
  nodes.forEach(n => { counts[n.kind] = (counts[n.kind] || 0) + (n.kind === 'apex' ? n.ends : 1); });
  const outlets = strokes.filter(s => s?.type === 'point' && lt(s) === 'outlet');
  const onGutter = outlets.filter(s => { const r = nearestGutterPoint(strokes, s.points[0]); return r && r.d <= tol; }).length;
  return { nodes, counts, outlets: outlets.length, outletsOnGutter: onGutter };
};

export const withStrokeIds = (strokes) => strokes.map(s => (s.id ? s : { ...s, id: `s_${Math.random().toString(36).slice(2, 10)}` }));
